// Session 53 · Ungeocoded Venues admin panel.
//
// Lists every venue whose most recent geocode attempt failed OR that lacks
// lat/lng entirely. Each row exposes:
//   - The current address (line 1, city, state, ZIP)
//   - Last geocode status + error + timestamp
//   - Retry button (single-attempt with current fields)
//   - Auto-resolve button (progressive fuzzy Nominatim ladder)
//   - Inline Nominatim autocomplete → replaces the whole address on select
//   - Save button that PUTs the venue and cascades to workspaces
//
// A "Batch auto-resolve all" button at the top runs the fallback ladder
// across every ungeocoded venue serially (Nominatim rate cap is 1 req/sec).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import NominatimAutocomplete from "../components/NominatimAutocomplete";

function StatusPill({ status }) {
  const map = {
    ok: { bg: "rgba(34,197,94,0.14)", fg: "#22c55e", label: "OK" },
    failed: { bg: "rgba(239,68,68,0.14)", fg: "#ef4444", label: "Failed" },
    error: { bg: "rgba(239,68,68,0.14)", fg: "#ef4444", label: "Error" },
    pending: { bg: "rgba(212,168,75,0.14)", fg: "#B8913A", label: "Pending" },
    skipped: { bg: "rgba(148,163,184,0.14)", fg: "#94a3b8", label: "Skipped" },
  };
  const meta = map[status] || { bg: "rgba(148,163,184,0.14)", fg: "#94a3b8", label: status || "unknown" };
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 999,
      background: meta.bg, color: meta.fg,
      fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600,
    }} data-testid={`ungeocoded-status-${status || "none"}`}>{meta.label}</span>
  );
}

function VenueRow({ v, onUpdate, onRefresh }) {
  const [draft, setDraft] = useState({
    address_line_1: v.address_line_1 || "",
    address_line_2: v.address_line_2 || "",
    city: v.city || "",
    county: v.county || "",
    state: v.state || "",
    postal_code: v.postal_code || "",
    country: v.country || "US",
    latitude: v.latitude,
    longitude: v.longitude,
  });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const dirty = useMemo(
    () => Object.keys(draft).some(k => (draft[k] ?? "") !== (v[k] ?? "")),
    [draft, v],
  );

  const retry = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/venues/${v.venue_id}/retry-geocode`);
      if (r.data?.status === "ok") {
        toast.success(`Geocoded: ${r.data.latitude?.toFixed(3)}, ${r.data.longitude?.toFixed(3)}`);
        onRefresh();
      } else {
        toast.error(`Retry failed: ${r.data?.error || "no match"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusy(false);
    }
  };

  const autoResolve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/venues/${v.venue_id}/auto-resolve`);
      if (r.data?.status === "ok") {
        toast.success(`Auto-resolved: ${r.data.latitude?.toFixed(3)}, ${r.data.longitude?.toFixed(3)}`);
        onRefresh();
      } else {
        toast.error(`Auto-resolve exhausted: ${r.data?.error || "no match"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-resolve failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/venues/${v.venue_id}`, draft);
      toast.success("Address saved — geocoding in background");
      onUpdate(v.venue_id, r.data);
      setTimeout(() => onRefresh(), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`ungeocoded-row-${v.venue_id}`}
      style={{
        background: "var(--navy-mid, #1a2340)",
        border: "1px solid rgba(212,168,75,0.16)",
        borderRadius: 12, padding: 16, marginBottom: 12,
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 240, flex: "1 1 320px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <Link to={`/admin/venues/${v.venue_id}/edit`} data-testid={`ungeocoded-name-${v.venue_id}`}
              style={{ color: "#fff", fontSize: 16, fontWeight: 600, textDecoration: "none" }}>
              {v.name}
            </Link>
            <StatusPill status={v.geocode_status} />
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>
            {v.address_line_1 || <span style={{ color: "#ef4444" }}>(no address)</span>}
            {v.address_line_2 && `, ${v.address_line_2}`}
            <br />
            {[v.city, v.state, v.postal_code].filter(Boolean).join(", ") || <span style={{ color: "#94a3b8" }}>(no city/state/ZIP)</span>}
          </div>
          {v.geocode_error && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#ef4444" }}>
              Last error: {v.geocode_error}
            </div>
          )}
          {v.geocoded_at && (
            <div style={{ marginTop: 4, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
              Last attempt: {new Date(v.geocoded_at).toLocaleString()}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" onClick={retry} disabled={busy || saving}
            data-testid={`ungeocoded-retry-${v.venue_id}`}
            style={{
              padding: "8px 14px", borderRadius: 8, cursor: "pointer",
              border: "1px solid rgba(212,168,75,0.28)", background: "transparent",
              color: "#B8913A", fontSize: 12, fontWeight: 600,
              opacity: (busy || saving) ? 0.55 : 1,
            }}>
            {busy ? "…" : "Retry"}
          </button>
          <button type="button" onClick={autoResolve} disabled={busy || saving}
            data-testid={`ungeocoded-auto-${v.venue_id}`}
            style={{
              padding: "8px 14px", borderRadius: 8, cursor: "pointer",
              border: "1px solid #B8913A", background: "#B8913A",
              color: "#fff", fontSize: 12, fontWeight: 600,
              opacity: (busy || saving) ? 0.55 : 1,
            }}>
            {busy ? "…" : "Auto-resolve"}
          </button>
          <button type="button" onClick={() => setExpanded(x => !x)}
            data-testid={`ungeocoded-edit-${v.venue_id}`}
            style={{
              padding: "8px 14px", borderRadius: 8, cursor: "pointer",
              border: "1px solid rgba(255,255,255,0.16)", background: "transparent",
              color: "rgba(255,255,255,0.8)", fontSize: 12, fontWeight: 600,
            }}>
            {expanded ? "Close" : "Edit address"}
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)" }}
          data-testid={`ungeocoded-editor-${v.venue_id}`}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: 8 }}>
            Type an address · OpenStreetMap autocomplete
          </div>
          <NominatimAutocomplete
            value={draft.address_line_1}
            onChange={(vv) => setDraft(d => ({ ...d, address_line_1: vv }))}
            testid={`ungeocoded-nom-${v.venue_id}`}
            onPick={(picked) => {
              setDraft(d => ({
                ...d,
                address_line_1: picked.address_line_1 || d.address_line_1,
                city: picked.city || d.city,
                county: picked.county ?? d.county,
                state: picked.state || d.state,
                postal_code: picked.postal_code || d.postal_code,
                country: picked.country || d.country,
                latitude: picked.latitude ?? d.latitude,
                longitude: picked.longitude ?? d.longitude,
              }));
              toast.success("Address staged — click Save to persist");
            }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginTop: 12 }}>
            {[
              ["address_line_2", "Suite / Apt"],
              ["city", "City"],
              ["county", "County"],
              ["state", "State"],
              ["postal_code", "ZIP"],
              ["country", "Country"],
            ].map(([k, label]) => (
              <div key={k}>
                <label style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", display: "block", marginBottom: 4 }}>
                  {label}
                </label>
                <input value={draft[k] || ""}
                  onChange={(e) => setDraft(d => ({ ...d, [k]: e.target.value }))}
                  data-testid={`ungeocoded-field-${k}-${v.venue_id}`}
                  style={{
                    width: "100%", background: "var(--navy-dark, #0f1729)",
                    border: "1px solid rgba(212,168,75,0.22)", borderRadius: 6,
                    color: "#fff", padding: "8px 10px", fontSize: 13,
                  }}/>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
            <button type="button" onClick={save} disabled={!dirty || saving}
              data-testid={`ungeocoded-save-${v.venue_id}`}
              style={{
                padding: "9px 18px", borderRadius: 8, cursor: dirty && !saving ? "pointer" : "not-allowed",
                border: "1px solid #B8913A", background: dirty ? "#B8913A" : "transparent",
                color: dirty ? "#fff" : "rgba(184,145,58,0.6)", fontSize: 13, fontWeight: 600,
                opacity: saving ? 0.55 : 1,
              }}>
              {saving ? "Saving…" : "Save & re-geocode"}
            </button>
            {draft.latitude && draft.longitude && (
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
                Staged coords: {Number(draft.latitude).toFixed(4)}, {Number(draft.longitude).toFixed(4)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminUngeocodedVenues() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/venues-ungeocoded");
      setVenues(r.data?.venues || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ungeocoded venues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const runBatch = async () => {
    if (!confirm(`Auto-resolve ${venues.length} venues? Takes ~${venues.length}s due to Nominatim's rate limit.`)) return;
    setBatchRunning(true);
    setBatchResult(null);
    try {
      const r = await api.post("/admin/venues-batch-auto-resolve", {}, { timeout: venues.length * 8000 + 30000 });
      setBatchResult(r.data);
      toast.success(`Batch done · ${r.data.resolved}/${r.data.total} resolved`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    } finally {
      setBatchRunning(false);
    }
  };

  const handleUpdate = (venueId, updated) => {
    setVenues(vs => vs.map(v => v.venue_id === venueId ? { ...v, ...updated, venue_id: venueId } : v));
  };

  return (
    <div className="m-container" style={{ padding: "40px 24px", maxWidth: 1080, margin: "0 auto" }}
        data-testid="admin-ungeocoded-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(212,168,75,0.85)", marginBottom: 6 }}>
            Data cleanup
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#fff", margin: 0 }}>
            Ungeocoded venues
          </h1>
          <div style={{ marginTop: 6, color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
            Venues without coordinates never appear on the map or in radius searches.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" onClick={refresh} disabled={loading}
            data-testid="ungeocoded-refresh"
            style={{
              padding: "10px 18px", borderRadius: 8, cursor: "pointer",
              border: "1px solid rgba(255,255,255,0.16)", background: "transparent",
              color: "rgba(255,255,255,0.8)", fontSize: 12, fontWeight: 600,
            }}>
            Refresh
          </button>
          <button type="button" onClick={runBatch} disabled={batchRunning || venues.length === 0}
            data-testid="ungeocoded-batch"
            style={{
              padding: "10px 20px", borderRadius: 8, cursor: batchRunning || venues.length === 0 ? "not-allowed" : "pointer",
              border: "1px solid #B8913A", background: batchRunning ? "transparent" : "#B8913A",
              color: batchRunning ? "#B8913A" : "#fff", fontSize: 12, fontWeight: 600,
              opacity: venues.length === 0 ? 0.5 : 1,
            }}>
            {batchRunning ? "Batch running…" : `Auto-resolve all (${venues.length})`}
          </button>
        </div>
      </div>

      {batchResult && (
        <div style={{
          background: "rgba(184,145,58,0.08)", border: "1px solid rgba(212,168,75,0.28)",
          borderRadius: 10, padding: 14, marginBottom: 20,
        }} data-testid="ungeocoded-batch-result">
          <div style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>
            Batch result: {batchResult.resolved} of {batchResult.total} resolved
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>
            {(batchResult.outcomes || []).filter(o => o.status !== "ok").map(o => o.name).join(" · ") || "All resolved!"}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 14 }} data-testid="ungeocoded-loading">
          Loading…
        </div>
      ) : venues.length === 0 ? (
        <div data-testid="ungeocoded-empty" style={{
          padding: 40, textAlign: "center",
          background: "rgba(34,197,94,0.06)",
          border: "1px solid rgba(34,197,94,0.28)", borderRadius: 12, color: "#22c55e",
        }}>
          🎉 Every venue is geocoded. Radius search covers 100% of the catalog.
        </div>
      ) : (
        <div data-testid="ungeocoded-list">
          {venues.map(v => (
            <VenueRow key={v.venue_id} v={v} onUpdate={handleUpdate} onRefresh={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}
